import { useCallback, useEffect, useState, memo } from "react";
import { Button, Division, DocumentViewer, EmptyState, Heading, Icon, Icons, Loader, Paragraph } from "@ir/ui";
import { GetDocumentSignedURLUseCase } from "@/core/usecases/document";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DocumentSignedURLRepository } from "@/infrastructure/repositories/document";
import { createLogger } from "@/infrastructure/services/logger";
import { useWindowSize } from "@/hooks/measurements/useWindowSize";
import { PromotionSourceDocument } from "@/core/entities/promotion";

interface PromotionDocumentViewerProps {
  document: PromotionSourceDocument;
  onClose: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const client = GraphQlAdapter.getInstance().getClient();
const documentSignedURLRepository = new DocumentSignedURLRepository(client);
const logger = createLogger("PromotionDocumentViewer");

const PromotionDocumentViewer = memo(({ document, onClose, className, style }: PromotionDocumentViewerProps) => {
  const { width, height } = useWindowSize();
  const [signedUrl, setSignedUrl] = useState<string | null>(document.signedUrl ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchDocumentSignedURL = useCallback(async (sourceDocument: PromotionSourceDocument): Promise<string | null> => {
    try {
      const response = await new GetDocumentSignedURLUseCase(documentSignedURLRepository).execute(
        sourceDocument.filePath,
        sourceDocument.fileMimeType,
        sourceDocument.fileDisplayName
      );

      return response.signedUrl;
    } catch (error) {
      logger.error("Error fetching promotion document signed URL", error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (width < 1200) {
      onClose();
    }
  }, [width, onClose]);

  useEffect(() => {
    let isMounted = true;

    const initializeDocument = async () => {
      setHasError(false);

      if (document.signedUrl) {
        setSignedUrl(document.signedUrl);
        return;
      }

      setIsLoading(true);
      const nextSignedUrl = await fetchDocumentSignedURL(document);

      if (!isMounted) {
        return;
      }

      setSignedUrl(nextSignedUrl);
      setHasError(!nextSignedUrl);
      setIsLoading(false);
    };

    void initializeDocument();

    return () => {
      isMounted = false;
    };
  }, [document, fetchDocumentSignedURL]);

  if (isLoading) {
    return (
      <Division className="promotion-document-viewer">
        <Loader loading={isLoading} />
      </Division>
    );
  }

  if (hasError || !signedUrl) {
    return (
      <Division className="promotion-document-viewer promotion-document-viewer-error bg-white border-left-1 border-color-grey-50">
        <Button
          size="medium"
          variant="clear"
          color="grey"
          className="promotion-document-viewer-close"
          onPress={onClose}
          aria-label="Close document viewer"
        >
          <Icon name={Icons.Close} size="medium" />
        </Button>
        <EmptyState
          className="promotion-document-viewer-empty px-4 py-5"
          icon={Icons.File}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              Unable to load document
            </Heading>
          }
          description={
            <Paragraph className="text-grey-500 font-regular mb-0">
              The promotion document could not be fetched. Please try again shortly.
            </Paragraph>
          }
        />
      </Division>
    );
  }

  return (
    <Division className="promotion-document-viewer">
      <DocumentViewer
        onClose={onClose}
        documents={[
          {
            id: 1,
            mimeType: document.fileMimeType,
            url: signedUrl,
            name: document.fileDisplayName,
          },
        ]}
        height={height}
        className={className}
        style={style}
      />
    </Division>
  );
});

PromotionDocumentViewer.displayName = "PromotionDocumentViewer";

export default PromotionDocumentViewer;
